import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/layout/PageHeader";
import { Button, Col, Form, Input, Pagination, Row, Select, Spin } from "antd";
import { CompanyListItemDto, UserCreateDto, UserDto, UserListItemDto } from "../generated-api";
import { useHistory } from "react-router-dom";
import { DEFAULT_PAGE_SIZE } from "../constants";
import {
    createUser,
    getUser,
    getUserSystemRoles,
    isCompanyAdmin,
    useUser,
    useUserCreate,
    useUserMe,
    useUserSystemRoles
} from "../hooks/user";
import { Filter, PlusCircle, Users } from "react-feather";
import { validatePassword } from "../utils/validator";
import { getCompany, getCompanyMe, useCompany, useCompanyGuid } from "../hooks/company";
import { BackButton } from "../components/layout/BackButton";
import { filtersSubject, useFilters } from "../hooks";
import { USER_STATUS_ACTIVE, USER_STATUS_DISABLED, USER_STATUS_TEMP_DISABLED } from "../constants/user";
import { list_of_company_types } from "../constants/company";

const pageSize = DEFAULT_PAGE_SIZE;

export function UsersPage() {
    const { t } = useTranslation();
    const history = useHistory();
    const userResponse = useUser();
    const users: UserListItemDto[] = userResponse.data || [];
    const rolesResponse = useUserSystemRoles();
    const roles = rolesResponse.data || [];
    const isUserCompanyAdmin = isCompanyAdmin();
    const userMeResponse = useUserMe();
    const userMe = userMeResponse.data || {};
    const [form] = Form.useForm();
    useEffect(() => {
        getUser().then().catch(console.error);
        if (!roles.length) {
            getUserSystemRoles().then().catch(console.error);
        }
        if (isUserCompanyAdmin) {
            getCompanyMe().then().catch(console.error);
        } else {
            getCompany().then().catch(console.error);
        }
    }, [true]);
    const companyResponse = useCompany();
    const companies = companyResponse.data || [];
    const companyMeResponse = useCompanyGuid();
    const company = companyMeResponse.data;
    const userCreateResponse = useUserCreate();
    const [page, setPage] = useState(1);
    const [addFormCollapsed, setAddFormCollapsed] = useState(true);
    const filters: any = useFilters();
    const [filterCollapsed, setFilterCollapsed] = useState(!filters.filterCompany);
    const [filterCompany, setFilterCompany] = useState<any>(filters.filterCompany);
    const [filterStatus, setFilterStatus] = useState<any>(filters.filterStatus);

    if (isCompanyAdmin() && company) {
        companies.push(company);
    }

    const validateMessages = {
        required: t('error.required'),
        string: {
            max: t('error.string.max')
        },
        types: {
            email: t('error.type.email')
        }
    };

    const onFinish = (values: any): void => {
        const { firstName, lastName, username, password } = values;
        createUser({ firstName, lastName, username, password, email: username } as UserCreateDto)
            .then((user: UserDto) => {
                history.push('/users/' + user.id);
            })
            .catch(console.error);
    };

    const filtered = users.filter(user =>
        (filterCompany ? user.companyId === filterCompany : true)
        &&
        (filterStatus ? (filterStatus.includes(user.userCompaniesRoles.every(x => x.status))) : true)
    );
    const processed = [];
    for (let i = (page - 1) * pageSize; i < page * pageSize; i++) {
        filtered[i] && processed.push(filtered[i]);
    }

    return (
        <Spin spinning={userResponse.loading || rolesResponse.loading}>
            <PageHeader />
            <div className={'top-block accent5-background'}>
                <div className={'flex-row'}>
                    <h1 className={'h1 flex-auto'}><BackButton /><Users />{t('users.title')}</h1>
                    {!isUserCompanyAdmin &&
                        <a href={'/users'} className={'title-icon-button mr-1 ' + (filterCollapsed ? '' : 'toggle')}
                            onClick={event => {
                                event.preventDefault();
                                setFilterCollapsed(!filterCollapsed);
                                setAddFormCollapsed(true);
                            }}>
                            <Filter />
                        </a>
                    }
                    <a href={'/users'} className={'title-icon-button ' + (addFormCollapsed ? '' : 'toggle')}
                        onClick={event => {
                            event.preventDefault();
                            setAddFormCollapsed(!addFormCollapsed);
                            setFilterCollapsed(true);
                            form.resetFields();
                            if (isUserCompanyAdmin) {
                                form.setFieldsValue({ companyId: userMe.companyId });
                            }
                        }}>
                        <PlusCircle className={addFormCollapsed ? '' : 'rotate45'} />
                    </a>
                </div>
                {!isUserCompanyAdmin &&
                    <Form className={filterCollapsed ? 'collapsed' : 'expanded'}>
                        <Row className={'row-offset'}>
                            <Col span={24} md={12} lg={8}>
                                <Form.Item className={'mb-0'}>
                                    <Select placeholder={t('user.filterCompany')} allowClear showSearch
                                        style={{ width: '100%' }}
                                        value={filterCompany}
                                        onChange={filterCompany => {
                                            setFilterCompany(filterCompany);
                                            filtersSubject.next({ filterCompany });
                                        }}
                                        filterOption={(input, option): any =>
                                            String((option || {}).children || '').toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
                                        }>
                                        {companies.map((company: CompanyListItemDto) =>
                                            <Select.Option key={company.id} value={String(company.id)}>
                                                {company.name}
                                            </Select.Option>
                                        )}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={24} md={12} lg={8}>
                                <Form.Item name={'status'}>
                                    <Select placeholder={t('user.status.title')}
                                        style={{ width: '100%' }}
                                        value={filterStatus}
                                        onChange={filterStatus => {
                                            const newFilterStatus = [filterStatus]
                                            if (filterStatus === USER_STATUS_DISABLED) {
                                                newFilterStatus.push(USER_STATUS_TEMP_DISABLED)
                                            }
                                            setFilterStatus(newFilterStatus);
                                            filtersSubject.next({ newFilterStatus });
                                        }}
                                    >
                                        <Select.Option key={'true'} value={USER_STATUS_ACTIVE}>
                                            {t('user.status.active')}
                                        </Select.Option>
                                        <Select.Option key={'false'} value={USER_STATUS_DISABLED}>
                                            {t('user.status.disabled')}
                                        </Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                }
                <Spin spinning={userCreateResponse.loading}>
                    <Form form={form} name="user" onFinish={onFinish} validateMessages={validateMessages}
                        autoComplete={'off'} className={addFormCollapsed ? 'collapsed' : 'expanded'}
                        validateTrigger={'onSubmit'}>
                        <Row className={'row-offset'}>
                            <Col span={12} md={8} lg={6}>
                                <Form.Item name={'firstName'} rules={[{ required: true }, { max: 256 }]}>
                                    <Input placeholder={t('user.firstName')} />
                                </Form.Item>
                            </Col>
                            <Col span={12} md={8} lg={6}>
                                <Form.Item name={'lastName'} rules={[{ required: true }, { max: 256 }]}>
                                    <Input placeholder={t('user.lastName')} />
                                </Form.Item>
                            </Col>
                            <Col span={24} md={8} lg={6}>
                                <Form.Item name={'username'} rules={[{ required: true }, { max: 256 }, { type: 'email' }]}>
                                    <Input placeholder={t('user.emailUsername')} />
                                </Form.Item>
                            </Col>
                            <Col span={12} md={8} lg={6}>
                                <Form.Item name={'password'} rules={[
                                    { required: true },
                                    () => ({ validator: (_, value) => validatePassword(value, t) })
                                ]}>
                                    <Input type={'password'} placeholder={t('user.password')} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item className={'no-margin'}>
                            <Button type="primary" className={'white-background text-default w-100'} htmlType="submit">
                                {t('users.add')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Spin>
            </div>
            <div className={'subpage-content'}>
                {processed.map((user: UserListItemDto, index: number) =>
                    <div key={index} className={`card accent5-background ${user.userCompaniesRoles.some(x => x.status === USER_STATUS_ACTIVE) ? '' : 'card-disabled'}`}>
                        <a href={'/users/' + user.id} onClick={event => {
                            event.preventDefault();
                            history.push('/users/' + user.id);
                        }}>
                            <div className={'card-header flex-row'}>
                                {user.firstName} {user.lastName}
                            </div>
                            <div className={'card-body'}>
                                {list_of_company_types.map((companyType, index: number) =>
                                    <div>
                                        <p className="row-title">{companyType.name} {t('applicationAccount')}</p>
                                        <Row className={'row-offset'}>
                                            <Col span={24} md={12} xl={8}>
                                                <label>
                                                    {t('user.company')}:
                                                </label>
                                                <p>
                                                    {user.userCompaniesRoles.find(x=>x.companyType === companyType.code)?.companyName ?? '-'}
                                                </p>
                                            </Col>
                                            <Col span={24} md={12} xl={8}>
                                                <label>
                                                    {t('user.roles')}:
                                                </label>
                                                <p>
                                                    {(user.userCompaniesRoles.find(x=>x.companyType === companyType.code)?.roles || []).map(item => t('user.role.' + item.role)).join(', ') ||
                                                        '-'}
                                                </p>
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                            </div>
                        </a>
                    </div>
                )}
                {!processed.length && <div className={'no-data'}>{t('general.noData')}</div>}
                {filtered.length > pageSize &&
                    <Pagination onChange={setPage} pageSize={pageSize} total={filtered.length} current={page}
                        defaultCurrent={page} />
                }
            </div>
        </Spin>
    )
}
